// Angular
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PaymentMethodPurchaseOrderRequestService } from '@core/services-v2/payment-method-purchase-order-request.service';
// PrimeNG
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputOtpModule } from 'primeng/inputotp';
// RxJS
import { interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
// Validators
import { OtpValidator } from './validators/otp.validator';
// Directives
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
import { InputData } from './models/input-data.model';

const NG_MODULES = [ReactiveFormsModule];
const PRIME_MODULES = [InputOtpModule, ButtonModule];
const DIRECTIVES = [FormSubmitDirective, ControlErrorsDirective];

@Component({
  selector: 'app-sms-modal',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, DIRECTIVES],
  templateUrl: './sms-modal.component.html',
  styleUrl: './sms-modal.component.scss',
})
export class SmsModalComponent {
  form!: FormGroup;
  value!: number;
  inputData!: InputData;

  private readonly fb = inject(FormBuilder);
  public readonly ref = inject(DynamicDialogRef);
  public readonly config = inject(DynamicDialogConfig);
  private readonly paymentMethodPurchaseOrderRequestService = inject(
    PaymentMethodPurchaseOrderRequestService
  );

  timeLeft: number = 90; // 1:30 minutos en segundos
  displayTime: string = '01:30';
  private timerSubscription?: Subscription;
  hasErrorOnResend = signal<boolean>(false);

  constructor() {
    this.buildForm();
    this.startTimer();
  }

  private buildForm(): void {
    this.form = this.fb.group({
      otp: [null, [OtpValidator.isValid]],
    });
  }

  ngOnInit(): void {
    this.inputData = this.config.data;
  }

  private startTimer(): void {
    this.timeLeft = 90;
    this.timerSubscription?.unsubscribe();

    this.timerSubscription = interval(1000)
      .pipe(take(this.timeLeft))
      .subscribe(() => {
        this.timeLeft--;
        const minutes = Math.floor(this.timeLeft / 60);
        const seconds = this.timeLeft % 60;
        this.displayTime = `${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`;

        if (this.timeLeft === 0) {
          this.timerSubscription?.unsubscribe();
        }
      });
  }

  ngOnDestroy(): void {
    this.timerSubscription?.unsubscribe();
  }

  onSubmit(): void {
    if (this.form.invalid || !this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const { otp } = this.form.value;
    this.paymentMethodPurchaseOrderRequestService
      .confirmOTP(this.inputData.purchaseOrderId, otp)
      .subscribe({
        next: (purchaseOrder) => {
          this.ref.close({ isValid: true, purchaseOrder });
        },
        error: (e) => {
          console.log(e);
        },
      });
  }

  /**
   * Reenviar OTP.
   */
  resendOtp(): void {
    this.paymentMethodPurchaseOrderRequestService
      .resendOTP(this.inputData.purchaseOrderId)
      .subscribe({
        next: (r) => {
          // TODO: transformar respuesta en void.
          this.hasErrorOnResend.set(false);
          this.startTimer();
        },
        error: (e) => {
          // Indicar que hubo un error al intentar reenviar el OTP.
          this.hasErrorOnResend.set(true);
          // Ha ocurrido un error al intentar reenviar el OTP. Por favor, intenta de nuevo más tarde o contacta a soporte.
          console.log(e);
        },
      });
  }
}
